import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import z from "zod";

import {
  type ExternalAccountDetailsAttributes,
  externalAccountDetailsSchema,
  externalAccountsEntityType,
  payoutSettingsAttributesSchema,
  payoutSettingsEntityType,
  payoutSettingsMetaSchema,
  payoutSettingsQuerySchema,
} from "./schema";

export const getPayoutSettingsResponseSchema = z.object({
  data: z.object({
    id: z.string(),
    type: z.literal(payoutSettingsEntityType),
    attributes: payoutSettingsAttributesSchema,
    meta: payoutSettingsMetaSchema,
  }),
  included: z.array(externalAccountDetailsSchema).optional(),
});
export type GetPayoutSettingsResponse = z.infer<typeof getPayoutSettingsResponseSchema>;

export function getPayoutSettingsUrl(workerId: string) {
  return `${environmentConfig.REACT_APP_PAYMENTS_API_URL}/accounts/${workerId}/payout-settings`;
}

export function getGetPayoutSettingsQueryKey(workerId: string, includeExternalAccount: boolean) {
  return [getPayoutSettingsUrl(workerId), includeExternalAccount];
}

export interface UseGetPayoutSettingsResponse {
  attributes: GetPayoutSettingsResponse["data"]["attributes"];
  meta: GetPayoutSettingsResponse["data"]["meta"];
  destinationDetails: ExternalAccountDetailsAttributes | undefined;
}

export function mapPayoutSettingsToResponseWithDestination(
  response: GetPayoutSettingsResponse,
  shouldIncludeDestination: boolean
): UseGetPayoutSettingsResponse {
  const { attributes, meta } = response.data;
  const destinationStripeId = attributes.preferredExternalAccountStripeId;
  const destinationDetails = shouldIncludeDestination
    ? response.included?.find((included) => included.id === destinationStripeId)?.attributes
    : undefined;

  return {
    attributes,
    meta,
    destinationDetails,
  };
}

export interface UseGetPayoutSettingsOptions {
  includeExternalAccount?: boolean;
}

export function useGetPayoutSettings(
  options: UseGetPayoutSettingsOptions = {}
): UseQueryResult<UseGetPayoutSettingsResponse> {
  const { includeExternalAccount = false } = options;
  const worker = useDefinedWorker();
  const url = getPayoutSettingsUrl(worker.userId);

  return useQuery({
    queryKey: getGetPayoutSettingsQueryKey(worker.userId, includeExternalAccount),
    queryFn: async () => {
      const response = await get({
        url,
        queryParams: includeExternalAccount ? { include: externalAccountsEntityType } : undefined,
        requestSchema: payoutSettingsQuerySchema,
        responseSchema: getPayoutSettingsResponseSchema,
      });

      return mapPayoutSettingsToResponseWithDestination(response.data, includeExternalAccount);
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.PAYOUT_SETTINGS_GET_SETTINGS_FAILURE,
    },
  });
}
